// GEOBAJA — Cola offline de escrituras (outbox en IndexedDB)
//
// Cimentación de la captura en campo sin señal: cuando una escritura a
// Supabase falla por red (o ya sabemos que no hay conexión), el intento se
// guarda aquí en orden FIFO y se re-ejecuta ("drena") al volver la red.
//
// Diseño:
// - FIFO estricto por `seq` autoincremental: los padres siempre viajan antes
//   que sus hijos (colado → muestras → especímenes) porque se encolaron antes.
// - El drenado NO conoce Supabase: supabaseClient.js le inyecta un ejecutor
//   via setExecutor(). Eso evita el import circular y mantiene este módulo
//   como mecánica pura de cola.
// - Idempotencia: la reejecución puede repetir un request que SÍ llegó al
//   servidor antes de cortarse la respuesta. El ejecutor decide qué errores
//   significan "ya estaba aplicado" (ej. 23505 en insert con UUID de cliente).
// - Resultado del ejecutor por item:
//     { ok: true }                  → aplicado; se borra del outbox
//     { ok: false, retriable: true } → problema de red; el drenado se detiene
//                                      y TODO lo pendiente espera a la próxima
//     { ok: false, error }          → rechazo real del servidor; el item se
//                                      marca 'error' (dead-letter) y se sigue
//                                      con el resto. Un hijo cuyo padre murió
//                                      caerá aquí también por su FK.
//     { ok: false, ajeno: true }    → la sesión vigente no es de quien capturó;
//                                      el item sigue pendiente y se continúa.
// - Web Locks (si existe) serializa el drenado entre pestañas; con UUIDs de
//   cliente un doble envío accidental es inofensivo de todos modos.
// - Eventos para la UX futura (badge "pendientes por subir"): subscribe(cb)
//   recibe { type, ... } en enqueued | drained | item-error | drain-idle | dueno.
// - DUEÑO (2026-09-16): cada registro guarda perfilId, la persona que capturó.
//   La cola solo sube lo de su dueño vigente (setDueno), que supabaseClient fija
//   cuando se ACEPTA un perfil, nunca al activar un token: antes, entrar con otra
//   cuenta en una tablet compartida subía las capturas pendientes del técnico
//   anterior con la identidad del nuevo. Lo ajeno se queda pendiente (no pasa a
//   dead-letter) hasta que su dueño vuelva a entrar. Los registros de antes, sin
//   perfilId, suben con quien esté, como siempre. Sin dueño no se drena nada.
//   Lo fallido tiene el mismo dueño: retryFailed solo regresa a la cola lo que sube
//   el dueño vigente, y getStatus cuenta aparte las fallidas de otra persona
//   (errorAjenos), que solo su dueño reintenta o descarta (components/OfflineStatus.js).
//
// Este módulo NO decide qué tablas son offline-capaces ni fabrica respuestas
// sintéticas: eso vive en supabaseClient.js, dueño del contrato con la API.

const DB_NAME = 'geobaja-offline';
const DB_VERSION = 1;
const STORE = 'outbox';

// ── IndexedDB mínimo (sin dependencias) ───────────────────────────────
let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'seq', autoIncrement: true });
        store.createIndex('status', 'status', { unique: false });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // Si otra versión de la app pide upgrade, soltar la conexión
      db.onversionchange = () => { db.close(); _dbPromise = null; };
      resolve(db);
    };
    req.onerror = () => { _dbPromise = null; reject(req.error); };
  });
  return _dbPromise;
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    let result;
    try { result = fn(store); } catch (e) { reject(e); return; }
    t.oncomplete = () => {
      // result puede ser un IDBRequest o un valor directo
      resolve(result && result.result !== undefined ? result.result : result);
    };
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

// ── Estado y eventos ──────────────────────────────────────────────────
const _listeners = new Set();
let _draining = false;
let _executor = null;          // (item) => Promise<{ok, retriable?, error?}>
let _retryTimer = null;
let _counts = { pending: 0, error: 0, ajenos: 0, errorAjenos: 0 }; // cache del último recuento
let _dueno = null;             // perfil aceptado cuya cola se puede subir
let _otraPasada = false;       // se pidió drenar mientras ya se drenaba

// Un registro sin perfilId (anterior al 2026-09-16) sube con cualquier dueño.
const puedeSubir = (item, dueno) => !item.perfilId || item.perfilId === dueno;
const esAjeno = (item, dueno) => item.status === 'pending' && !!item.perfilId && item.perfilId !== dueno;
const esFallidaAjena = (item, dueno) => item.status === 'error' && !!item.perfilId && item.perfilId !== dueno;

function emit(evt) {
  _listeners.forEach((cb) => { try { cb(evt); } catch (e) {} });
}

async function refreshCounts() {
  try {
    const db = await openDB();
    const items = await tx(db, 'readonly', (s) => s.getAll());
    const list = items || [];
    _counts = {
      pending: list.filter((i) => i.status === 'pending').length,
      error: list.filter((i) => i.status === 'error').length,
      ajenos: list.filter((i) => esAjeno(i, _dueno)).length,
      errorAjenos: list.filter((i) => esFallidaAjena(i, _dueno)).length,
    };
  } catch (e) { /* IDB inaccesible: conservar el último recuento */ }
  return _counts;
}

// ── API pública ───────────────────────────────────────────────────────

/** Guarda un intento de escritura. item: {table, op, payload, filter, conflictCol, perfilId} */
async function enqueue(item) {
  const db = await openDB();
  const record = {
    ...item,
    perfilId: item.perfilId || null,
    status: 'pending',
    tries: 0,
    lastError: null,
    createdAt: new Date().toISOString(),
  };
  const seq = await tx(db, 'readwrite', (s) => s.add(record));
  _counts.pending += 1;
  if (esAjeno(record, _dueno)) _counts.ajenos += 1;
  emit({ type: 'enqueued', seq, table: item.table, op: item.op, counts: { ..._counts } });
  scheduleRetry();
  return seq;
}

/** Inyectado por supabaseClient: cómo re-ejecutar un item contra la API. */
function setExecutor(fn) { _executor = fn; }

/**
 * Recuento cacheado {pending, error, ajenos, errorAjenos} + dueño y bandera de drenado.
 * ajenos = pendientes de otra persona (no suben con el dueño vigente).
 * errorAjenos = fallidas de otra persona (siguen contando en error; solo su dueño decide).
 */
function getStatus() {
  return { ..._counts, dueno: _dueno, draining: _draining, online: typeof navigator === 'undefined' ? true : navigator.onLine };
}

/**
 * Fija la persona cuya cola puede subir (null = nadie). Lo llama supabaseClient
 * al aceptar un perfil y al cerrar sesión. Si el dueño nuevo tiene pendientes,
 * drena: así una sesión restaurada sube lo suyo sin esperar otro evento.
 */
function setDueno(perfilId) {
  const nuevo = perfilId || null;
  if (nuevo === _dueno) return;
  _dueno = nuevo;
  refreshCounts().then((counts) => {
    if (_dueno !== nuevo) return; // otro cambio de dueño llegó antes
    emit({ type: 'dueno', dueno: nuevo, counts: { ...counts } });
    if (nuevo && counts.pending - counts.ajenos > 0) drain();
  });
}

/** Suscripción a eventos de la cola. Devuelve el unsubscribe. */
function subscribe(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}

/** Items en estado 'error' (dead-letter) para inspección/reintento manual. */
async function getFailed() {
  const db = await openDB();
  const items = await tx(db, 'readonly', (s) => s.getAll());
  return (items || []).filter((i) => i.status === 'error');
}

/** Items pendientes por subir, en orden FIFO (para la UX de estado). */
async function getPending() {
  const db = await openDB();
  const items = await tx(db, 'readonly', (s) => s.getAll());
  return (items || []).filter((i) => i.status === 'pending');
}

/**
 * Reintenta los items 'error' (los regresa a 'pending') y drena. Solo los que sube
 * el dueño vigente, o `perfilId` si se pasa: las fallidas de otra persona se quedan
 * como están hasta que su dueño decida. Sin dueño, solo las que no tienen dueño
 * (anteriores al 2026-09-16).
 */
async function retryFailed(opciones) {
  const explicito = !!opciones && typeof opciones === 'object' && 'perfilId' in opciones;
  const dueno = explicito ? (opciones.perfilId || null) : _dueno;
  const db = await openDB();
  const items = await tx(db, 'readonly', (s) => s.getAll());
  const failed = (items || []).filter((i) => i.status === 'error' && puedeSubir(i, dueno));
  for (const item of failed) {
    await tx(db, 'readwrite', (s) => s.put({ ...item, status: 'pending', lastError: null }));
  }
  await refreshCounts();
  // Notificar SIEMPRE (sin red el drain aborta y nadie más emitiría: el chip
  // debe pasar de rojo a ámbar con el conteo nuevo de inmediato).
  emit({ type: 'retried', counts: { ..._counts } });
  return drain(explicito ? { perfilId: dueno } : undefined);
}

/** Descarta definitivamente un item (decisión del usuario en la UX futura). */
async function discard(seq) {
  const db = await openDB();
  await tx(db, 'readwrite', (s) => s.delete(seq));
  await refreshCounts();
  emit({ type: 'discarded', seq, counts: { ..._counts } });
}

// ── Drenado ───────────────────────────────────────────────────────────

async function drainLoop(dueno) {
  if (!_executor) return 'idle';
  const db = await openDB();
  // Snapshot ordenado por seq (keyPath autoincrement → getAll ya viene en orden).
  // Solo lo del dueño (y lo anterior a los dueños): lo ajeno se salta sin tocarlo.
  const items = await tx(db, 'readonly', (s) => s.getAll());
  const pending = (items || []).filter((i) => i.status === 'pending' && puedeSubir(i, dueno));
  if (!pending.length) { emit({ type: 'drain-idle', counts: { ..._counts } }); return 'idle'; }

  for (const item of pending) {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return 'stopped'; // se perdió la red a medio drenado
    let res;
    try { res = await _executor(item); }
    catch (e) { res = { ok: false, retriable: true }; } // el ejecutor jamás debería lanzar; tratar como red
    if (res && res.ajeno) {
      // La sesión vigente no es de quien capturó (cambió a media cola): se queda
      // pendiente, sin sumar intentos, y se sigue con lo demás.
      continue;
    }
    if (res && res.ok) {
      await tx(db, 'readwrite', (s) => s.delete(item.seq));
      await refreshCounts();
      emit({ type: 'drained', seq: item.seq, table: item.table, op: item.op, counts: { ..._counts } });
    } else if (res && res.retriable) {
      // Red caída: conservar 'pending' y abortar; el próximo online reintenta
      await tx(db, 'readwrite', (s) => s.put({ ...item, tries: (item.tries || 0) + 1 }));
      scheduleRetry();
      return 'stopped';
    } else {
      // Rechazo real del servidor: dead-letter y seguir con el resto
      const lastError = (res && res.error) || 'rechazado';
      await tx(db, 'readwrite', (s) => s.put({ ...item, status: 'error', tries: (item.tries || 0) + 1, lastError }));
      await refreshCounts();
      emit({ type: 'item-error', seq: item.seq, table: item.table, op: item.op, error: lastError, counts: { ..._counts } });
    }
  }
  emit({ type: 'drain-idle', counts: { ..._counts } });
  return 'done';
}

/**
 * Dispara un drenado de la cola del dueño vigente, o de `perfilId` si se pasa.
 * No-op sin dueño, sin red o si ya hay uno corriendo (en ese caso da otra pasada
 * al terminar). Devuelve 'hecho' | 'sin_dueno' | 'sin_red' | 'ocupado'.
 */
async function drain(opciones) {
  const explicito = !!opciones && typeof opciones === 'object' && 'perfilId' in opciones;
  const dueno = explicito ? (opciones.perfilId || null) : _dueno;
  if (!dueno) return 'sin_dueno';
  if (_draining) { _otraPasada = true; return 'ocupado'; }
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 'sin_red';
  _draining = true;
  try {
    const run = async () => {
      // Si durante el drenado se encolaron items nuevos (captura en curso
      // mientras subía), dar pases extra hasta vaciar — con tope defensivo.
      for (let pass = 0; pass < 10; pass++) {
        const result = await drainLoop(dueno);
        if (result !== 'done') break;
        await refreshCounts();
        if (_counts.pending - _counts.ajenos <= 0) break;
      }
    };
    // Web Locks serializa entre pestañas; si otra pestaña ya drena, salir
    if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
      await navigator.locks.request('geobaja-outbox-drain', { ifAvailable: true }, async (lock) => {
        if (lock) await run();
      });
    } else {
      await run();
    }
  } catch (e) { /* IDB o lock fallaron: el retry programado reintenta */ }
  finally {
    _draining = false;
    if (_otraPasada) {
      _otraPasada = false;
      setTimeout(() => { drain(); }, 0);
    }
  }
  return 'hecho';
}

// Reintento suave mientras haya pendientes (cubre el caso "online según el
// navegador, pero el fetch falló" — señal débil en campo).
function scheduleRetry() {
  if (_retryTimer) return;
  _retryTimer = setTimeout(() => {
    _retryTimer = null;
    if (_counts.pending > 0) drain();
  }, 30000);
}

/** Llamar una vez al arrancar la app (supabaseClient lo hace en init). */
async function init() {
  if (typeof window === 'undefined' || !('indexedDB' in window)) return;
  await refreshCounts();
  // Primer recuento real (arranque con pendientes persistidos): notificar a
  // quien ya esté suscrito — sin esto el chip arranca en 0 hasta el
  // siguiente evento.
  emit({ type: 'init', counts: { ..._counts } });
  window.addEventListener('online', () => drain());
  // Al volver de segundo plano en la tablet (pantalla apagada en obra)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && _counts.pending > 0) drain();
  });
  // El almacenamiento persistente evita que Android purgue el outbox
  if (navigator.storage && navigator.storage.persist) {
    try { navigator.storage.persist(); } catch (e) {}
  }
  if (_counts.pending > 0) drain();
}

export const outbox = {
  enqueue,
  setExecutor,
  setDueno,
  init,
  drain,
  getStatus,
  subscribe,
  getFailed,
  getPending,
  retryFailed,
  discard,
};
